import { Check, LoaderCircle, X } from 'lucide-react';
import { AgentDot } from '../../AgentDot';
import { statusInk, taskStatusBadge } from '../../../lib/task-status';
import type { TaskStep } from '../../../lib/use-task-detail';
import taskStyles from './TaskDetailsPanel.module.css';

type StatusVariant = ReturnType<typeof taskStatusBadge>['variant'];

// The agent-activity timeline shared by both task-detail surfaces: the working agent, then each step —
// done ✓, or, for the step in motion (the first incomplete one), the mark the design gives that status
// (design-QA Figma 13523-219741…220082): failed ✕, a solid #FF773A dot for action-needed, a static grey
// dot for waiting-on-a-reply, NO mark while paused, and a spinner for in progress — the spinner only
// turns while a worker loop is genuinely live (`isLive`). Upcoming steps are bare.
export function TaskActivityTimeline({
  agentId,
  agentName,
  steps,
  currentStep,
  statusVariant,
  isLive,
}: {
  agentId: string;
  agentName: string;
  steps: TaskStep[];
  currentStep: number;
  statusVariant: StatusVariant;
  /** A worker loop is executing on this task right now — the only condition that animates the marker. */
  isLive: boolean;
}) {
  if (steps.length === 0) {
    return null;
  }
  const currentMark =
    statusVariant === 'failed' ? (
      <X size={16} style={{ color: statusInk('failed') }} />
    ) : statusVariant === 'waiting-on-you' ? (
      // Action needed — a solid dot in the pending orange (#FF773A / --ds-text-pending), not the badge's
      // darker text ink.
      <span className={taskStyles.actionDot} style={{ background: 'var(--ds-text-pending)' }} />
    ) : statusVariant === 'paused' ? null : statusVariant === 'waiting-on-a-reply' ? (
      // Parked on a reply/timer — static, neutral. Nothing is running, so nothing moves.
      <span className={taskStyles.waitDot} style={{ background: statusInk('waiting-on-a-reply') }} />
    ) : (
      // In progress — a spinner in the secondary ink; it only turns while a worker loop is genuinely live.
      <LoaderCircle size={16} className={isLive ? taskStyles.spin : undefined} style={{ color: 'var(--ds-text-secondary)' }} />
    );
  return (
    <section className={taskStyles.activity}>
      {agentName && (
        <div className={taskStyles.agent}>
          <AgentDot id={agentId} size={15} />
          <span className={taskStyles.agentName}>{agentName}</span>
        </div>
      )}
      <ol className={taskStyles.timeline}>
        {steps.map((step, i) => {
          // A done step (✓) and a failed step read Regular; every other step — the active dot/spinner and
          // the upcoming ones — reads Medium, both in the secondary ink (design-QA Figma 13523-219915/999).
          const regular = step.completed || statusVariant === 'failed';
          return (
            <li key={i} className={taskStyles.step}>
              <span aria-hidden className={taskStyles.stepMark}>
                {step.completed ? <Check size={16} /> : i === currentStep ? currentMark : null}
              </span>
              <span className={`${taskStyles.stepText} ${regular ? taskStyles.stepTextRegular : ''}`.trim()}>
                {step.title}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
